export type BudgetFrequency =
  | "weekly"
  | "2weekly"
  | "4weekly"
  | "monthly"
  | "quarterly"
  | "semiannual"
  | "yearly";

/**
 * Normalize the various frequency representations we see across:
 * - UI schema values (weekly, 2weekly, 4weekly, monthly...)
 * - API codes (W, 2W, 4W, M, Q, SA, Y)
 * - Legacy/other variants (week/month/year, "fortnightly", "6 monthly", etc.)
 */
export function normalizeBudgetFrequency(value: unknown): BudgetFrequency | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;

  const upper = raw.toUpperCase();
  // API mode codes (and a couple of legacy debt variants)
  if (upper === "W") return "weekly";
  if (upper === "2W") return "2weekly";
  if (upper === "4W" || upper === "FW" || upper === "F") return "4weekly";
  if (upper === "M") return "monthly";
  if (upper === "Q") return "quarterly";
  if (upper === "Y" || upper === "A") return "yearly";
  if (upper === "SA" || upper === "6M") return "semiannual";

  const norm = raw.toLowerCase().replace(/\s+/g, "").replace(/-/g, "");

  if (norm === "week" || norm === "weekly" || norm === "perweek") return "weekly";
  if (
    norm === "2weekly" ||
    norm === "twoweekly" ||
    norm === "fortnightly" ||
    norm === "per2weeks"
  )
    return "2weekly";
  if (norm === "4weekly" || norm === "fourweekly" || norm === "per4weeks")
    return "4weekly";
  if (norm === "month" || norm === "monthly" || norm === "permonth") return "monthly";
  if (norm === "quarter" || norm === "quarterly" || norm === "perquarter")
    return "quarterly";

  // Half-yearly / twice per year
  if (
    norm === "semiannual" ||
    norm === "semiannually" ||
    norm === "halfyearly" ||
    norm === "halfyear" ||
    norm === "biannual" ||
    norm === "biannually" ||
    norm === "6monthly" ||
    norm === "sixmonthly" ||
    norm === "6month" ||
    norm === "sixmonth" ||
    norm === "twiceyearly"
  )
    return "semiannual";

  if (
    norm === "year" ||
    norm === "yearly" ||
    norm === "annual" ||
    norm === "annually" ||
    norm === "peryear"
  )
    return "yearly";

  return undefined;
}

/**
 * Convert a budget amount to a monthly equivalent.
 *
 * This matches the budget help text no need to match legacy
If frequency = W then ((amount / 7) * 365) / 12

If frequency = 2W then ((amount / 14) * 365) / 12

If frequency = 4W then ((amount / 28) * 365) / 12

If frequency = Q then (amount / 3)

If frequency = SA then (amount / 6)

If frequency = Y then (amount / 12)
 */
export function toMonthlyBudgetAmount(
  amount: number,
  frequency: BudgetFrequency = "monthly"
): number {
  switch (frequency) {
    case "weekly":
      return ((amount/7) * 365) / 12;
    case "2weekly":
      return ((amount/14) * 365) / 12;
    case "4weekly":
      return ((amount/28) * 365) / 12;
    case "quarterly":
      return amount / 3;
    case "semiannual":
      return amount / 6;
    case "yearly":
      return amount / 12;
    case "monthly":
    default:
      return amount;
  }
}


